/** Links de concorrentes do ML (13/10/2026): exportar planilha → editar → importar (prévia e confirmação) e atualizar
 * os preços dos links de catálogo pela API. Validação e gravação no serviço (core/links_concorrentes.py). */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Link, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, Typography,
} from '@mui/material'
import { Seta } from './componentes'
import { atualizarLinks, carregarLinks, importarLinks, URL_EXPORTAR_LINKS, type LinkConcorrente, type PreviaLinks } from './fonte'
import { brl2, num } from './logic'

const TIPO: Record<string, string> = { catalogo: 'catálogo', anuncio: 'anúncio', user_product: 'página de produto', desconhecido: '?' }
const quando = (t: string | null) => (t ? `${t.slice(8, 10)}/${t.slice(5, 7)}${t.length > 10 ? ` ${t.slice(11, 16)}` : ''}` : '—')

export function LinksConcorrentes({ onMsg }: { onMsg: (m: string) => void }) {
  const [linhas, setLinhas] = useState<LinkConcorrente[] | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [aberto, setAberto] = useState(false)
  const [ocupado, setOcupado] = useState(false)
  const [arquivo, setArquivo] = useState<File | null>(null)
  const [previa, setPrevia] = useState<PreviaLinks | null>(null)
  const entrada = useRef<HTMLInputElement>(null)
  const recarregar = useCallback(() => {
    carregarLinks().then((r) => { setLinhas(r.linhas); setErro(null) }).catch((e: unknown) => setErro(e instanceof Error ? e.message : String(e)))
  }, [])
  useEffect(() => { recarregar() }, [recarregar])
  const resumo = useMemo(() => {
    const l = linhas ?? []
    return { total: l.length, skus: new Set(l.map((x) => x.sku)).size, auto: l.filter((x) => x.automatico).length,
      semPreco: l.filter((x) => x.mediana == null).length, ultima: l.filter((x) => x.automatico).map((x) => x.coletado_em ?? '').sort().pop() || null }
  }, [linhas])

  const atualizar = async () => {
    setOcupado(true); setErro(null)
    try { onMsg(`${await atualizarLinks()} O quadro de preço recalcula em segundo plano.`); recarregar() } catch (e) { setErro(e instanceof Error ? e.message : String(e)) } finally { setOcupado(false) }
  }
  const escolher = async (f: File | null) => {
    if (entrada.current) entrada.current.value = ''
    if (!f) return
    setOcupado(true); setErro(null); setArquivo(f)
    try { setPrevia(await importarLinks(f, false)) } catch (e) { setErro(e instanceof Error ? e.message : String(e)); setArquivo(null) } finally { setOcupado(false) }
  }
  const confirmar = async () => {
    if (!arquivo) return
    setOcupado(true); setErro(null)
    try {
      const r = await importarLinks(arquivo, true)
      onMsg(`Planilha importada: ${r.mantidos} links mantidos, ${r.novos.length} novos, ${r.removidos.length} removidos${r.precos_informados ? `, ${r.precos_informados} preços informados` : ''}. Registrado no Log de decisões; o quadro de preço recalcula em segundo plano.`)
      setPrevia(null); setArquivo(null); recarregar()
    } catch (e) { setErro(e instanceof Error ? e.message : String(e)) } finally { setOcupado(false) }
  }

  return (
    <Card variant="outlined" sx={{ minWidth: 0 }}><CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <Box>
          <Typography variant="h6">Links de concorrentes</Typography>
          <Typography variant="caption" color="text.secondary">
            {linhas ? `${num(resumo.total)} links em ${resumo.skus} SKUs · ${resumo.auto} de catálogo (atualizados pelo botão${resumo.ultima ? `, último em ${quando(resumo.ultima)}` : ''}) · ${resumo.total - resumo.auto} de anúncio/página de produto (coleta local ou preço na planilha)${resumo.semPreco ? ` · ${resumo.semPreco} sem preço` : ''}` : 'carregando…'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button size="small" variant="outlined" href={URL_EXPORTAR_LINKS}>Exportar planilha</Button>
          <Button size="small" variant="outlined" component="label" disabled={ocupado}>Importar planilha
            <input ref={entrada} hidden type="file" accept=".xlsx" onChange={(e) => void escolher(e.target.files?.[0] ?? null)} /></Button>
          <Button size="small" variant="contained" disabled={ocupado} onClick={() => void atualizar()}>{ocupado ? 'Aguarde…' : 'Atualizar preços'}</Button>
        </Stack>
      </Stack>
      {erro && <Alert severity="error" sx={{ mb: 1 }}>{erro}</Alert>}
      <Button size="small" onClick={() => setAberto(!aberto)} startIcon={<Seta aberta={aberto} />}>{aberto ? 'Esconder a lista' : 'Ver a lista'}</Button>
      {aberto && linhas && (
        <Box sx={{ overflow: 'auto', maxHeight: '50vh', mt: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead><TableRow>{['SKU', 'Link', 'Tipo', 'Mediana', 'Preços', 'Coletado em', 'Situação'].map((h, i) => <TableCell key={h} align={[3, 4].includes(i) ? 'right' : 'left'} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</TableCell>)}</TableRow></TableHead>
            <TableBody>
              {linhas.map((l) => (
                <TableRow key={`${l.sku}|${l.link}`} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}><b>{l.sku}</b></TableCell>
                  <TableCell sx={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><Link href={l.link} target="_blank" rel="noopener noreferrer">{l.link.replace(/^https:\/\/(www\.|produto\.)?/, '')}</Link></TableCell>
                  <TableCell>{TIPO[l.tipo] ?? l.tipo}</TableCell>
                  <TableCell align="right">{brl2(l.mediana)}</TableCell>
                  <TableCell align="right">{l.n}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{quando(l.coletado_em)}{!l.automatico && l.fonte ? <Typography component="span" variant="caption" color="text.secondary"> · {l.fonte === 'planilha' ? 'planilha' : 'coleta local'}</Typography> : null}</TableCell>
                  <TableCell><Chip size="small" variant="outlined" color={l.situacao === 'ok' ? 'success' : l.situacao.startsWith('erro') ? 'error' : 'warning'} label={l.situacao} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <Dialog open={!!previa} onClose={() => !ocupado && (setPrevia(null), setArquivo(null))} maxWidth="md" fullWidth>
        <DialogTitle>Importar planilha de links</DialogTitle>
        <DialogContent>
          {previa && (
            <Stack spacing={1.5} sx={{ mt: 0.5 }}>
              <Typography variant="body2"><b>{previa.mantidos}</b> mantidos · <b>{previa.novos.length}</b> novos · <b>{previa.removidos.length}</b> removidos · <b>{previa.recusadas.length}</b> linhas com problema{previa.precos_informados ? ` · ${previa.precos_informados} preços informados` : ''}</Typography>
              <Alert severity="warning">A planilha substitui a lista inteira: os removidos saem do painel.</Alert>
              {[['Novos', previa.novos], ['Removidos', previa.removidos]].map(([t, l]) => (l as { sku: string; link: string }[]).length > 0 && (
                <Box key={t as string}><Typography variant="subtitle2">{t as string}</Typography>
                  {(l as { sku: string; link: string }[]).map((x) => <Typography key={x.sku + x.link} variant="caption" component="div" sx={{ wordBreak: 'break-all' }}>{x.sku} · {x.link}</Typography>)}</Box>
              ))}
              {previa.recusadas.length > 0 && (
                <Box><Typography variant="subtitle2">Linhas com problema</Typography>
                  {previa.recusadas.map((x) => <Typography key={x.linha} variant="caption" component="div" color="error.main" sx={{ wordBreak: 'break-all' }}>Linha {x.linha}: {x.sku || '(sem SKU)'} · {x.motivo}</Typography>)}</Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPrevia(null); setArquivo(null) }} disabled={ocupado}>Cancelar</Button>
          <Button variant="contained" onClick={() => void confirmar()} disabled={ocupado || !previa || previa.mantidos + previa.novos.length === 0}>{ocupado ? 'Gravando…' : 'Confirmar importação'}</Button>
        </DialogActions>
      </Dialog>
    </CardContent></Card>
  )
}
